import {
  UploadStatus,
  UploadWidgetListItem,
} from '@aus-platform/design-system';
import { isNil } from 'lodash';
import type React from 'react';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  selectUploadWidget,
  setFilesToUpload,
  setUploadStatusList,
} from 'shared/upload-widget-slices';
import type { UploadFileSetItem } from 'shared/upload-widget-slices/types';
import { useImageSetUpload } from 'src/processing-workflow/upload-images';
import { activeUploadStatuses, finishedUploadStatuses } from './constants';
import { UploadWidgetItemActions } from './upload-widget-item-actions';

export type UploadWidgetFileSetProps = {
  uploadItem: UploadFileSetItem;
  isCancelledAllUploads: boolean;
  setIsCancelledAllUploads: React.Dispatch<React.SetStateAction<boolean>>;
};

export const UploadWidgetFileSet: React.FC<UploadWidgetFileSetProps> = ({
  uploadItem,
  isCancelledAllUploads,
  setIsCancelledAllUploads,
}) => {
  // Dispatchers.
  const dispatch = useAppDispatch();

  // Selectors.
  const { uploadStatusList, filesToUpload } =
    useAppSelector(selectUploadWidget);

  // Variables.
  const currentUpload = uploadStatusList.find(({ id }) => id === uploadItem.id);

  // Hooks.
  const { pauseUpload, resumeUpload, cancelUpload } = useImageSetUpload(
    uploadItem,
    currentUpload?.uploadStatus,
  );

  // useEffects.
  // Follows the file rows' "Cancel all" flag from the header.
  useEffect(() => {
    if (
      isCancelledAllUploads &&
      !isNil(currentUpload) &&
      activeUploadStatuses.includes(currentUpload.uploadStatus)
    ) {
      cancelUpload();
      setIsCancelledAllUploads(false);
    }
  }, [isCancelledAllUploads]);

  // Handlers.
  const handleCancelUpload = () => {
    if (
      !isNil(currentUpload) &&
      finishedUploadStatuses.includes(currentUpload.uploadStatus)
    ) {
      dispatch(
        setFilesToUpload(
          filesToUpload.filter(({ id }) => id !== uploadItem.id),
        ),
      );
      dispatch(
        setUploadStatusList((uploadStatusList) =>
          uploadStatusList.filter(({ id }) => id !== uploadItem.id),
        ),
      );
      return;
    }

    cancelUpload();
  };

  if (isNil(currentUpload)) {
    return null;
  }

  return (
    <UploadWidgetListItem
      fileName={currentUpload.fileName}
      uploadStatus={currentUpload.uploadStatus}
      progress={currentUpload.progress}
      speed={currentUpload.speed}
      timeLeft={
        currentUpload.uploadStatus === UploadStatus.Completed
          ? currentUpload.timeTaken
          : currentUpload.timeLeft
      }
      counts={currentUpload.counts}
      error={currentUpload.error}
    >
      <UploadWidgetItemActions
        uploadStatus={currentUpload.uploadStatus}
        fileDetails={uploadItem.fileDetails}
        onCancel={handleCancelUpload}
        onPause={pauseUpload}
        onResume={resumeUpload}
      />
    </UploadWidgetListItem>
  );
};
